import * as React from 'react';
import { useSelector } from 'react-redux';
import { NumberFormat as Format } from '@lightspeed/i18n-number-format';

import { getCfdOptions } from '~/renderer/store/cfd/selectors';
import { isClientSideReady } from '~/renderer/utils';
import { defaultCurrency } from '~/renderer/utils/currency';

import type { Options } from '~/renderer/store/cfd/types';
import type { AppState } from '~/renderer/store/types';

interface Parts {
  code: string;
  decimal: string;
  formattedCurrency: string;
  formattedNumber: string;
  fraction: number;
  number: number;
  symbol: string;
  wholeNumber: number;
}

// Load the correct NumberFormat depending on the CFD environment
const NumberFormat = (isClientSideReady() && window.NumberFormat) || Format;

export const useMoney = () => {
  const { locale, currencyCode } = useSelector<AppState, Options>(getCfdOptions);
  const [formatter, setFormatter] = React.useState<Format>(new NumberFormat({ locale }));
  React.useEffect(() => {
    setFormatter(new NumberFormat({ locale }));
  }, [locale]);
  return {
    money: (money: string | number | undefined) => {
      const value = money ?? '0';
      return formatter.format(parseFloat(String(value)), {
        style: 'currency',
        currencyCode: currencyCode ?? defaultCurrency.code,
      }) as string;
    },
    moneyParts: (money: string | number | undefined) => {
      const value = money ?? '0';
      return formatter.getParts(String(value), {
        style: 'currency',
        currencyCode: currencyCode ?? defaultCurrency.code,
      }) as Parts;
    },
    parseMoney: (moneyString: string | undefined) => {
      const value = moneyString ?? '0';
      return formatter.parse(value, {
        style: 'currency',
        currencyCode: currencyCode ?? defaultCurrency.code,
      }) as number;
    },
  };
};
